import React, { useCallback, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { Input, Popover, message } from 'antd';
import clsx from 'clsx';
import log from 'shared/utils/log';
import { Conversation } from 'sb-lib/types';
import { useAppDispatch, useAppSelector } from 'shared/store';
import { selectActiveOrgMembers } from 'shared/store/auth-slice';
import { assignConversation } from 'shared/store/conversation-slice';
import UserAvatar from 'shared/components/UserAvatar';
import DropdownArrowIcon from 'shared/assets/images/dropdown-arrow-icon.svg';
import { Icon } from 'shared/components/Icon';
import './style.less';

interface ConversationAssignButtonProps {
  conversation: Conversation | null;
  callback?: () => void;
}

export const ConversationAssignButton: React.FC<ConversationAssignButtonProps> = ({
  conversation,
  callback,
}) => {
  const dispatch = useAppDispatch();
  const members = useAppSelector(selectActiveOrgMembers);
  const [search, setSearch] = useState('');
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const assignee = conversation?.assigneeId
    ? members.find(m => m.id === conversation.assigneeId)
    : null;

  const handleAssign = useCallback(
    async (memberId: number) => {
      if (!conversation) return;
      setIsPopoverVisible(false);
      try {
        const assigneeId = memberId === conversation?.assigneeId ? null : memberId;
        const res = await dispatch(
          assignConversation({ conversationId: conversation?.id, assigneeId }),
        );
        unwrapResult(res);
        if (callback) {
          callback();
        }
      } catch (err) {
        log.error('Failed to assign conversation', undefined, err);
        message.error(err.message || 'Failed to assign conversation');
      }
    },
    [callback, conversation, dispatch],
  );

  return (
    <Popover
      visible={isPopoverVisible}
      onVisibleChange={() => setIsPopoverVisible(false)}
      placement="bottomRight"
      overlayClassName="ConversationAssignButton__overlay"
      content={
        <div
          className="ConversationAssignButton"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className="ConversationAssignButton__title">Assignee</div>
          <div className="ConversationAssignButton__search">
            <Input
              allowClear
              type="search"
              placeholder="Search..."
              onChange={e => setSearch(e.target.value)}
              prefix={
                <Icon icon="search" className="ConversationAssignButton__search-icon" />
              }
            />
          </div>
          <div className="ConversationAssignButton__members">
            {members
              .filter(m => {
                if (!search) return true;
                return m.displayName.toLowerCase().includes(search.toLowerCase());
              })
              .map(m => {
                return (
                  <button
                    key={m.id}
                    className={clsx(
                      'ConversationAssignButton__member',
                      m.id === conversation?.assigneeId &&
                        'ConversationAssignButton__member--active',
                    )}
                    onClick={() => handleAssign(m.id)}
                  >
                    <UserAvatar
                      className="ConversationAssignButton__member-avatar"
                      user={m}
                      size={32}
                    />
                    <div className="ConversationAssignButton__member-name">
                      {m.displayName}
                    </div>
                  </button>
                );
              })}
          </div>
        </div>
      }
    >
      <button
        className="ConversationAssignButton__assignee"
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          setIsPopoverVisible(!isPopoverVisible);
        }}
      >
        {assignee ? (
          <>
            <UserAvatar
              className="ConversationAssignButton__assignee-avatar"
              user={assignee}
              size={16}
            />
            <div className="ConversationAssignButton__assignee-name">
              {assignee.displayName}
            </div>
          </>
        ) : (
          <>
            Assignee{' '}
            <DropdownArrowIcon className="ConversationAssignButton__assignee-arrow" />
          </>
        )}
      </button>
    </Popover>
  );
};
